import useSWR from "swr";

import { api, NH_SIGN_IN_URL, PlanInfo, UserMe } from "../api";
import { Icon, PageHeader } from "../ui";

const SCOPE: Record<string, string> = { curated: "Curated funds", active_only: "Active funds", all: "All 7,000+ funds" };

export const fundAccess = (p: PlanInfo) =>
  p.max_funds ? `Up to ${p.max_funds} funds a quarter` : SCOPE[p.fund_scope] ?? p.fund_scope;
export const historyLabel = (p: PlanInfo) => (p.history_years ? `${p.history_years} years` : "Full history");

export const INDICES_LINE = "Every plan includes API access to the monthly NilssonHedge indices.";

// The plans we sell (NilssonHedge's option 1), keyed by tier name. Names and limits come from the
// API; this is the pitch and price around them. Basic is what a NilssonHedge subscription grants
// (the handoff's "paid" tier) and Plus is its paid upgrade. Enterprise stays unlisted: custom deals
// go through "Talk to us".
const COPY: Record<string, { price: string; per: string; tagline: string; extras?: string[] }> = {
  free: { price: "$0", per: "No card needed", tagline: "Explore the data, no strings attached." },
  basic: {
    price: "Included",
    per: "in your NilssonHedge subscription",
    tagline: "API access for the funds you follow, at no extra cost.",
  },
  plus: {
    price: "$450",
    per: "a year, or $45 a month with a 12-month minimum",
    tagline: "The full database and an AI assistant, for NilssonHedge subscribers.",
    extras: ["AI assistant (MCP)"],
  },
  pro: {
    price: "$4,990",
    per: "a year, or $499 a month with a 12-month minimum",
    tagline: "Your research desk, built on NilssonHedge.",
    extras: ["Premium AI assistant (MCP)", "Excel add-in", "Factsheets, tools and alerts", "Monthly research"],
  },
};

// One item per limit, so a card can list only the lines that differ from the plan before it.
const features = (p: PlanInfo) =>
  [
    fundAccess(p),
    p.daily_indices ? "Daily indices" : "Monthly NilssonHedge indices",
    p.history_years ? `${p.history_years} years of history` : "Full history",
    "Watchlists",
    p.can_bulk_export && "CSV export",
    `${p.requests_per_day.toLocaleString()} API requests a day`,
  ].filter((f): f is string => !!f);

const YES = <span className="text-pos">✓<span className="sr-only">Yes</span></span>;
const has = (extra: string) => (p: PlanInfo) => (COPY[p.name].extras?.includes(extra) ? YES : "—");
const MCP: Record<string, string> = { plus: "Basic", pro: "Premium" };

const ROWS: [string, (p: PlanInfo) => React.ReactNode][] = [
  ["Fund access", fundAccess],
  ["Indices", (p) => (p.daily_indices ? "Daily & monthly" : "Monthly")],
  ["History", historyLabel],
  ["CSV export", (p) => (p.can_bulk_export ? YES : "—")],
  ["AI assistant (MCP)", (p) => MCP[p.name] ?? "—"],
  ["Excel add-in", has("Excel add-in")],
  ["Factsheets, tools and alerts", has("Factsheets, tools and alerts")],
  ["Monthly research", has("Monthly research")],
  ["Results per request", (p) => p.max_results_per_page.toLocaleString()],
  ["Requests / minute", (p) => p.requests_per_minute.toLocaleString()],
  ["Requests / hour", (p) => p.requests_per_hour.toLocaleString()],
  ["Requests / day", (p) => p.requests_per_day.toLocaleString()],
];

const TALK = "/contact?interest=pro";

/** The plans on sale, cheapest first. SWR shares the fetch between the cards and the table. */
export function usePlans() {
  const { data, error } = useSWR<PlanInfo[]>("/plans", (path: string) => api.get<PlanInfo[]>(path));
  return { plans: data?.filter((p) => p.name in COPY), error };
}

function cta(p: PlanInfo, user: UserMe | null) {
  if (p.name === user?.tier.name) return <span className="py-2 text-sm font-medium text-muted">Your current plan</span>;
  if (p.name === "free") return user ? null : <a href="/#sign-in" className="btn-default text-main">Start free</a>;
  if (p.name === "basic") return <a href={NH_SIGN_IN_URL} className="btn-default text-main">Continue with NilssonHedge</a>;
  if (p.name === "plus") return <a href="/contact?interest=subscriber" className="btn-default text-main">Talk to us</a>;
  return <a href={TALK} className="btn-primary text-white">Talk to us</a>;
}

/** Plan cards, on /pricing and the landing page. Each card after the first lists only what it adds. */
export function PlanCards({ plans, user }: { plans: PlanInfo[]; user: UserMe | null }) {
  return (
    <ul className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
      {plans.map((p, i) => {
        const copy = COPY[p.name];
        const prev = plans[i - 1];
        const before = prev ? features(prev) : [];
        const pro = p.name === "pro";
        return (
          <li
            key={p.name}
            className={`relative flex flex-col p-7 rounded-xl border ${pro ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}
          >
            {pro && (
              <span className="absolute -top-3 left-7 px-2.5 py-0.5 rounded-full bg-accent text-white text-[11px] font-semibold uppercase tracking-wider">
                Recommended
              </span>
            )}
            <h3 className="text-xl font-bold tracking-tight text-main">{p.display_name}</h3>
            <p className="mt-4 text-3xl font-bold tracking-tight text-main">{copy.price}</p>
            <p className="mt-1 text-xs text-muted">{copy.per}</p>
            <p className="mt-4 text-sm text-muted">{copy.tagline}</p>
            <p className="mt-6 text-xs font-semibold text-muted">
              {prev ? `Everything in ${prev.display_name}, plus:` : "Includes:"}
            </p>
            <ul className="mt-3 mb-8 flex flex-col gap-2.5 text-sm text-main">
              {features(p).filter((f) => !before.includes(f)).map((f) => (
                <li key={f} className="flex gap-2.5">
                  <span aria-hidden className="text-accent font-bold">✓</span>
                  {f}
                </li>
              ))}
              {copy.extras?.map((f) => (
                <li key={f} className="flex gap-2.5 font-semibold">
                  <Icon name="sparkles" className="w-4 h-4 mt-0.5 text-accent" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-auto flex">{cta(p, user)}</div>
          </li>
        );
      })}
    </ul>
  );
}

/** Public plan comparison. `user` is null when logged out. */
export function Pricing({ user }: { user: UserMe | null }) {
  const { plans, error } = usePlans();
  const current = user?.tier.name;

  return (
    <section className="flex flex-col gap-12">
      <PageHeader
        title="Plans"
        description={`${INDICES_LINE} Start free, get the API with your NilssonHedge subscription, add the full database with Subscriber Plus, or choose Institutional to bring the data into your AI assistant and Excel.`}
      />

      {error && <p className="text-sm text-neg">Could not load plans.</p>}
      {!plans && !error && <p className="text-sm text-muted">Loading…</p>}
      {plans && (
        <>
          <PlanCards plans={plans} user={user} />

          <div>
            <h2 className="text-lg font-semibold text-main">Compare plans</h2>
            <p className="mt-1 mb-4 text-sm text-muted">What each plan includes, side by side.</p>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th><span className="sr-only">Feature</span></th>
                    {plans.map((p) => (
                      <th key={p.name} className={`text-center ${p.name === current ? "text-accent" : ""}`}>
                        {p.display_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([label, cell]) => (
                    <tr key={label}>
                      <td className="text-main font-medium whitespace-nowrap">{label}</td>
                      {plans.map((p) => (
                        <td key={p.name} className={`text-center whitespace-nowrap ${p.name === current ? "bg-accent/5 text-main font-medium" : ""}`}>
                          {cell(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <p className="text-sm text-muted">
        Need custom limits or a team plan? <a href="/contact?interest=custom">Talk to us</a>.
      </p>
    </section>
  );
}
